import React, { useRef } from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SectionTitle from '../components/SectionTitle';
import CTA from '../components/CTA';
import {
  FloatingFurniture,
  Sawdust,
  WoodRings,
  DovetailDivider,
  PendulumLamp,
  SET_A,
  SET_B,
} from '../components/Decor';
import useSmoothScroll from '../hooks/useSmoothScroll';
import useScrollFx from '../hooks/useScrollFx';
import { SERVICES, ADDITIONAL_SERVICES } from '../data/content';
import '../css/Services.css';
import '../css/ServicesFx.css';
import '../css/HomeDecor.css';
import '../css/ServicesDecor.css';

function ServiceRow({ service, index, offset = 0 }) {
  const reversed = index % 2 === 1;
  const num = String(index + 1 + offset).padStart(2, '0');

  return (
    <div className={`service-row sv-row ${reversed ? 'service-row--reversed' : ''}`}>
      <span className="sv-row__num" data-parallax="0.3" aria-hidden="true">{num}</span>

      <div className="service-row__media fx-clip">
        <img data-parallax="0.1" src={service.image} alt={service.title} loading="lazy" />
        <span className="service-row__icon">{service.icon}</span>
      </div>

      <div className="service-row__content">
        <h3>{service.title}</h3>
        <p>{service.description}</p>
        <ul className="service-row__features">
          {service.features.map((feature) => (
            <li key={feature}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7" stroke="var(--color-accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Services() {
  const root = useRef(null);
  useSmoothScroll();

  useScrollFx(root, (gsap) => {
    gsap.registerPlugin(ScrollTrigger);
    const scope = root.current;

    // Hero intro
    const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
    tl.from('.sv-hero__img', { scale: 1.35, duration: 2, ease: 'power3.out' })
      .from('.sv-hero .hero__eyebrow', { y: 20, opacity: 0, duration: 0.8 }, 0.3)
      .from('.sv-line > span', { yPercent: 115, duration: 1.2 }, 0.4)
      .from('.sv-hero p', { y: 30, opacity: 0, duration: 0.9 }, 0.9);

    // Hero parallax
    const st = { trigger: '.sv-hero', start: 'top top', end: 'bottom top', scrub: true };
    gsap.to('.sv-hero__img', { yPercent: 22, scale: 1.18, ease: 'none', scrollTrigger: st });
    gsap.to('.sv-hero__inner', { yPercent: -20, opacity: 0, ease: 'none', scrollTrigger: st });

    // Each service row: content slides from the opposite side, features cascade, icon spins in
    gsap.utils.toArray('.sv-row').forEach((row) => {
      const reversed = row.classList.contains('service-row--reversed');
      const dir = reversed ? -1 : 1;
      const icon = row.querySelector('.service-row__icon');
      const c = row.querySelector('.service-row__content');

      const t = gsap.timeline({
        scrollTrigger: { trigger: row, start: 'top 75%', once: true },
        defaults: { ease: 'power3.out' },
      });
      t.from(c.querySelector('h3'), { x: 70 * dir, opacity: 0, duration: 0.9, clearProps: 'transform,opacity' })
        .from(c.querySelector('p'), { x: 50 * dir, opacity: 0, duration: 0.9, clearProps: 'transform,opacity' }, 0.1)
        .from(c.querySelectorAll('li'), {
          x: 40 * dir, opacity: 0, duration: 0.7, stagger: 0.1, clearProps: 'transform,opacity',
        }, 0.25)
        .from(c.querySelectorAll('li svg'), {
          scale: 0, rotate: -90, duration: 0.5, stagger: 0.1, ease: 'back.out(2.4)', clearProps: 'transform',
        }, 0.35)
        .from(icon, { scale: 0, rotate: -200, duration: 0.8, ease: 'back.out(1.8)', clearProps: 'transform' }, 0.2);
    });

    // ---------- Furniture-themed effects ----------
    gsap.to('.scroll-progress', {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: { start: 0, end: 'max', scrub: 0.3 },
    });

    // Dovetail joints draw themselves
    scope.querySelectorAll('.dovetail path').forEach((p) => {
      const len = p.getTotalLength();
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
      gsap.to(p, {
        strokeDashoffset: 0,
        duration: 2.2,
        ease: 'power2.inOut',
        scrollTrigger: { trigger: p, start: 'top 95%', once: true },
      });
    });

    // Tree rings rotate with scroll
    scope.querySelectorAll('[data-spin]').forEach((el) => {
      gsap.to(el, {
        rotation: parseFloat(el.dataset.spin),
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });

    // Furniture line-art drifts for depth
    scope.querySelectorAll('.decor-float').forEach((el, i) => {
      const dir = i % 2 ? 1 : -1;
      gsap.fromTo(
        el,
        { y: 70 * dir },
        {
          y: -70 * dir,
          ease: 'none',
          scrollTrigger: {
            trigger: el.closest('section') || el.parentElement,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        },
      );
    });
  });

  return (
    <div ref={root}>
      <div className="scroll-progress" aria-hidden="true" />

      <section className="page-hero sv-hero">
        <img
          className="sv-hero__img"
          src="/assets/worker.png"
          alt=""
          loading="eager"
        />
        <div className="sv-hero__overlay" />
        <Sawdust count={22} />
        <PendulumLamp />
        <div className="container sv-hero__inner">
          <span className="hero__eyebrow">What We Build</span>
          <h1>
            <span className="sv-line"><span>Our Services</span></span>
          </h1>
          <p>Custom wooden furniture, repair, and finishing services from our workshop — built with solid timber and hand-finished detail.</p>
        </div>
      </section>

      <section className="section sv-section decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <WoodRings />
        <FloatingFurniture items={SET_A} />
        <span className="sv-bigword" data-drift="-16" aria-hidden="true">Crafted · Crafted · Crafted</span>
        <div className="container">
          <SectionTitle
            eyebrow="Furniture Manufacturing"
            title="Custom Pieces for Every Room"
            description="Every service below is fully customisable to your wood preference, dimensions, and finish."
          />
          <div className="service-rows">
            {SERVICES.map((service, i) => (
              <ServiceRow service={service} index={i} key={service.key} />
            ))}
          </div>
        </div>
      </section>

      <DovetailDivider />

      <section className="section sv-section decor-host" style={{ backgroundColor: '#f2ece1' }}>
        <div className="decor-grain" aria-hidden="true" />
        <WoodRings className="wood-rings--left" />
        <FloatingFurniture items={SET_B} />
        <div className="container">
          <SectionTitle
            eyebrow="Care & Restoration"
            title="Repair, Polishing & Carving"
            description="Beyond manufacturing, we restore and refinish existing wooden furniture."
          />
          <div className="service-rows">
            {ADDITIONAL_SERVICES.map((service, i) => (
              <ServiceRow service={service} index={i} offset={SERVICES.length} key={service.key} />
            ))}
          </div>
        </div>
      </section>

      <DovetailDivider flip />

      <section className="section">
        <CTA
          eyebrow="Start Your Project"
          title="Tell Us What You Need Built"
          description="Share your room dimensions and style preferences — we'll design and quote your furniture."
        />
      </section>
    </div>
  );
}